<template>
    <NodeViewWrapper class="listings-block" contenteditable="false">
        <div class="listings-header">
            <h3>📋 Property Listings Block</h3>
            <p>
                {{ node.attrs.listings.length }} {{ node.attrs.listings.length === 1 ? "property" : "properties" }} will
                be included in the email
            </p>
            <button @click="deleteNode" class="delete-button">
                <i class="fas fa-trash"></i>
            </button>
        </div>

        <div class="listings-preview">
            <div
                v-for="listing in node.attrs.listings.slice(0, 6)"
                :key="listing.listingId"
                class="listing-card-preview"
            >
                <img :src="getImageUrl(listing)" :alt="getAddressLine(listing)" class="listing-image" />
                <div class="listing-info">
                    <div class="listing-status" :style="getStatusStyle(listing)">
                        {{ getStatusText(listing) }}
                    </div>
                    <div class="listing-price">{{ getFormattedPrice(listing) }}</div>
                    <div class="listing-address">{{ getAddressLine(listing) }}</div>
                    <div class="listing-id">ID: {{ listing.listingId }}</div>
                </div>
            </div>

            <div v-if="node.attrs.listings.length > 6" class="more-listings">
                +{{ node.attrs.listings.length - 6 }} more properties
            </div>
        </div>
    </NodeViewWrapper>
</template>

<script setup lang="ts">
import { nodeViewProps, NodeViewWrapper } from "@tiptap/vue-3"

const props = defineProps(nodeViewProps)

// Helper function to get a value from listing or its attributes
const getValue = (listing: any, key: string): string => {
    const val = listing[key] || listing.attributes?.[key] || ""
    return String(val)
}

const getImageUrl = (listing: any): string => {
    return (
        listing.media?.[0]?.mediaURL ||
        listing.attributes?.media?.[0]?.mediaURL ||
        require("@/assets/icons/no_image.png")
    )
}

const getAddressLine = (listing: any): string => {
    return [
        getValue(listing, "streetNumber"),
        getValue(listing, "streetName"),
        getValue(listing, "streetSuffix"),
        listing.city,
        listing.stateOrProvince,
        getValue(listing, "postalCode"),
    ]
        .filter(Boolean)
        .join(" ")
}

const getFormattedPrice = (listing: any): string => {
    const price = getValue(listing, "listPrice")
    if (price) {
        return `$${Number(price).toLocaleString()}`
    }
    if (listing.attributes?.listPrice) {
        return `$${listing.attributes.listPrice.toLocaleString()}`
    }
    return "Contact for Price"
}

const getStatusText = (listing: any): string => {
    return getValue(listing, "standardStatus") || "No status"
}

const getStatusStyle = (listing: any) => {
    let backgroundColor = "#FEE2E2" // Default red background
    let color = "#991B1B" // Default red text

    if (listing.standardStatus === "Active" || listing.standardStatus === "Active Under Contract") {
        backgroundColor = "#D1FAE5" // Light green
        color = "#065F46" // Dark green
    } else if (listing.standardStatus === "Pending") {
        backgroundColor = "#FFEDD5" // Light orange
        color = "#C2410C" // Dark orange
    } else {
        backgroundColor = "#FEE2E2" // Red background for closed/other statuses
        color = "#991B1B" // Red text for closed/other statuses
    }

    return {
        backgroundColor,
        color,
    }
}

const deleteNode = () => {
    props.deleteNode()
}
</script>

<style scoped>
.listings-block {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    background-color: #f9fafb;
    user-select: none;
    display: block;
}

.listings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
}

.listings-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
}

.listings-header p {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
}

.delete-button {
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
}

.delete-button:hover {
    background: #dc2626;
}

.listings-preview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.listing-card-preview {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.listing-image {
    width: 100%;
    height: 120px;
    object-fit: cover;
    display: block;
    aspect-ratio: 16/9;
    min-height: 120px;
    max-height: 120px;
}

.listing-info {
    padding: 8px;
}

.listing-status {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    margin-bottom: 4px;
}

.listing-price {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 2px;
}

.listing-address {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 2px;
}

.listing-id {
    font-size: 11px;
    color: #9ca3af;
}

.more-listings {
    grid-column: 1 / -1;
    text-align: center;
    padding: 12px;
    background: #f3f4f6;
    border-radius: 6px;
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
}
</style>
